import { Player, Story } from "../../../types";
import StoryButton from "../../common/story-button/story-button";
import Avatar from "../../common/avatar/avatar";
import { saveFile } from "../../../utils/fileSaver";
import { format } from "date-fns";
import { now } from "../../../time";
import { constants } from "../../../constants";
import ResultsAvatar from "./results-avatar";
import CanvasView from "../../common/canvas-view/canvas-view";
import { storyToText } from "./story-results";
import { useAppSelector } from "../../../redux/store";
import GamePage from "../game-page/game-page";

import { cleanStoryName } from "./story-view";
import { useTranslation } from "react-i18next";
import Button from "@/components/ui/button";

type ResultsProps = {
  onGameStats: () => void;
  onStoryClick: (index: number) => void;
};

type Rank = {
  player?: Player;
  playerRank?: number;
  story?: Story;
  storyRank?: number;
};

const Leaderboard = ({ onGameStats, onStoryClick }: ResultsProps) => {
  const gameState = useAppSelector((state) => state.game);
  const roomCode = useAppSelector((state) => state.menu.playerId?.room_code);

  // Debug logging to help identify the issue
  // console.log('Debug - playerId:', useAppSelector(state => state.menu.playerId))
  // console.log('Debug - roomCode:', roomCode)

  const stories = gameState.game?.stories
    ?.slice()
    .sort((a, b) => b.meta.points - a.meta.points);

  const players = gameState.game?.players
    ?.slice()
    .sort(
      (a, b) =>
        (b.data.points ? b.data.points : 0) -
        (a.data.points ? a.data.points : 0)
    );

  const { t } = useTranslation("game");

  // Get ranking badge color: 1st place gets bright color, others get light color
  function getRankingColor(index: number, type: 'story' | 'player'): string {
    const isFirstPlace = index === 0;
    if (type === 'story') {
      return isFirstPlace
        ? 'bg-[var(--color-border-blue)]'      // Bright cyan for 1st
        : 'bg-[var(--color-light-ltBlue)]';    // Light blue for 2nd+
    } else {
      return isFirstPlace
        ? 'bg-[var(--color-border-magenta)]'   // Bright magenta for 1st
        : 'bg-[var(--color-light-ltPink)]';    // Light pink for 2nd+
    }
  }


  function playAgain() {
    window.open("https://franticfanfic.com", "_self");
  }

  const footer = (
    <>
      <Button className={"text-lg"} onClick={(_) => playAgain()}>
        {t("storyResults.footers.default")}
      </Button>
      <Button className={"text-sm flex-grow mx-1"} onClick={handleSupportUs}>
        {t("storyResults.footers.likeGame")}
        <br />
        {t("storyResults.footers.supportUs")}
      </Button>
      <Button className={"text-lg"} onClick={handleGameStats}>
        {t("storyResults.footers.gameStats")}
      </Button>
    </>
  );

  function storyAndPlayerRanks(): Rank[] {
    const size = Math.max(stories?.length ?? 0, players?.length ?? 0);

    let storyRank = 1,
      playerRank = 1;
    let prevStoryPoints = 0,
      prevPlayerPoints = 0;

    let storyRankRunner = 0;
    let playerRankRunner = 0;

    let ranks: Rank[] = [];
    for (let i = 0; i < size; i++) {
      const rank: Rank = {};

      if (stories && stories[i]) {
        rank.story = stories[i];

        if (stories[i].meta.points === prevStoryPoints) {
          rank.storyRank = storyRank;
          storyRankRunner++;
        } else {
          rank.storyRank = storyRank + storyRankRunner;
          storyRankRunner = 1;
        }
        prevStoryPoints = stories[i].meta.points;
        storyRank = rank.storyRank;
      }
      if (players && players[i]) {
        rank.player = players[i];

        if (players[i].data.points === prevPlayerPoints) {
          rank.playerRank = playerRank;
          playerRankRunner++;
        } else {
          rank.playerRank = playerRank + playerRankRunner;
          playerRankRunner = 1;
        }
        prevPlayerPoints = players[i].data.points;
        playerRank = rank.playerRank;
      }
      ranks.push(rank);
    }

    return ranks;
  }

  function handleSupportUs() {
    window.open("https://ko-fi.com/franticfanfic", "_blank");
  }

  function handleGameStats() {
    onGameStats();
  }

  async function handleBulkDownloadImages() {
    const [jszipModule, html2canvasModule] = await Promise.all([
      import("jszip"),
      import("html2canvas"),
    ]);
    const JSZip = jszipModule.default;
    const html2canvas = html2canvasModule.default;

    let zip = new JSZip();

    if (!gameState.game?.stories) return;

    const promises = gameState.game.stories.map(async (s) => {
      const canvasEl = document.querySelector(`#canvas-${s.id}`) as HTMLElement;
      if (!canvasEl) return;
      const canvas = await html2canvas(canvasEl, { scrollY: -window.scrollY });
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const filename = `${s.meta.index + 1}-${cleanStoryName(s)}`;
        zip.file(
          `${filename.replace(/ /g, "_").replace(/\./g, "_")}.png`,
          blob
        );
      }
    });

    await Promise.all(promises);
    const content = await zip.generateAsync({ type: "blob" });
    await saveFile(content, fileName("Images"));
  }

  async function handleBulkDownloadText() {
    const { default: JSZip } = await import("jszip");
    let zip = new JSZip();

    gameState.game?.stories?.forEach((s) => {
      const filename = `${s.meta.index + 1}-${cleanStoryName(s)}`;
      zip.file(
        filename.replace(/ /g, "_").replace(/\./g, "_") + ".txt",
        storyToText(s)
      );
    });

    const content = await zip.generateAsync({ type: "blob" });
    await saveFile(content, fileName("Texts"));
  }

  function fileName(label: string) {
    // Try multiple fallback sources for the room code
    const code = roomCode || localStorage.getItem("roomCode") || "UNKNOWN";
    return `FranticFanfic_${code}_${format(now(), "LLL_dd_yyyy")}_${label}.zip`;
  }

  function placingTitle(rank: number, points?: number) {
    if (rank === 1) {
      return `${constants.star} #${rank} - ${points} ${constants.star}`;
    } else {
      return `#${rank} - ${points}`;
    }
  }

  function subheaders() {
    if (!gameState.game?.settings.solo_writing) {
      return [
        t("storyResults.subheaders.react"),
        t("storyResults.subheaders.click"),
        t("storyResults.subheaders.download"),
      ];
    } else {
      return [t("storyResults.subheaders.downloadSolo")];
    }
  }

  return (
    <GamePage
      footerJsx={footer}
      titles={[t("storyResults.headers.default")]}
      subHeaders={subheaders()}
      hack={gameState.game?.stories?.map((s) => (
        <CanvasView key={"canvas-" + s.id} story={s} />
      ))}
    >
      <div className="flex flex-col">
        <div className="flex flex-row">
          <div className="text-white bg-black text-center p-1 text-base flex-grow-[3]">
            FANFICS
          </div>
          <div className="text-white bg-black text-center p-1 text-base flex-grow">
            WRITERS
          </div>
        </div>

        {storyAndPlayerRanks().map((value, idx) => {
          let story = value.story;
          let storyRank = value.storyRank;
          let player = value.player;
          let playerRank = value.playerRank;

          return (
            <div key={"row-" + idx} className="self-stretch flex flex-row items-stretch border-b-[thick] border-b-black">
              {story && storyRank !== undefined && (
                <div
                  key={"story-results-" + idx}
                  className="border-r-[thick] border-r-black flex flex-col justify-between w-[70%]"
                >
                  <div
                    className={`shadow-[2px_2px_black] p-1 text-center ${getRankingColor(idx, 'story')}`}
                  >
                    {placingTitle(storyRank, story ? story.meta.points : 0)}
                  </div>
                  <StoryButton
                    story={story}
                    onClick={() => onStoryClick(idx)}
                    showReactions
                    showAwards
                    size="base"
                  />
                </div>
              )}
              {player && playerRank !== undefined && (
                <div
                  key={"player-" + idx}
                  className="border-l-[thick] border-l-black flex flex-col justify-center pb-4 w-[30%] min-h-[15rem]"
                >
                  <div
                    className={`mb-auto text-center shadow-[2px_2px_black] p-1 ${getRankingColor(idx, 'player')}`}
                  >
                    {placingTitle(playerRank, player ? player.data.points : 0)}
                  </div>
                  <div className="text-[2rem] flex-grow h-16 flex flex-col items-center justify-center">
                    <Avatar
                      spriteId={player ? player.meta.sprite_id : 0}
                      name={player ? player.name : ""}
                      size="small"
                      variant="basic"
                    />
                    <ResultsAvatar player={player} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex flex-col border-t-[thick] border-t-black border-b-[thick] border-b-black">
        <div className="flex flex-row justify-around py-2">
          <Button
            className={"text-base sm:text-xl sm:mx-4"}
            onClick={handleBulkDownloadImages}
          >
            Download Images
          </Button>
          <Button
            className={"text-base sm:text-xl sm:mx-4"}
            onClick={handleBulkDownloadText}
          >
            Download Texts
          </Button>
        </div>
      </div>
    </GamePage>
  );
};

export default Leaderboard;
